import { API_PATH, BASE_PATH, API_BASE_URL } from './config';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Box,
    Typography,
    Button,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloseIcon from '@mui/icons-material/Close';
import { X, Calendar } from 'lucide-react';
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';


// Builds initial state from an existing activity for edit mode
function buildEditState(activity, tripDays, trip) {
    const hasFixedDates = Boolean(trip?.start_date && trip?.end_date);

    let typeStr = activity.type;
    if (typeStr === 0 || typeStr === '0' || !typeStr ||
        typeStr === 'ai_suggestion' || typeStr === 'add_activity' || typeStr === 'activity') typeStr = 'activity';
    if (typeStr === 1 || typeStr === '1' || typeStr === 'lodging' || typeStr === 'Lodging') typeStr = 'logging';
    if (typeStr === 2 || typeStr === '2') typeStr = 'transportation';
    if (typeStr === 'flight') typeStr = 'transportation';

    const resolvedType = typeStr === 'logging' ? 'Logging' : typeStr === 'transportation' ? 'Transportation' : 'Activity';
    const displayType = (!hasFixedDates && resolvedType !== 'Activity') ? 'Activity' : resolvedType;
    const resolvedDay = tripDays.find(d => d.dayNumber === activity.day)?.date || '';
    const transType = (typeStr === 'transportation' || activity.type === 'flight') ? (activity.subtype || 'flight') : 'flight';

    let structured = activity.structured_data;
    if (typeof structured === 'string') {
        try { structured = JSON.parse(structured); } catch (e) { structured = {}; }
    }
    if (Array.isArray(structured)) {
        structured = structured.length > 0 ? structured[0] : {};
    }
    if (!structured) structured = {};

    let formState = { duration: 30, flightType: 'one-way' };

    if (typeStr === 'activity') {
        let duration = activity.duration;
        if (!duration && activity.start_time && activity.end_time) {
            const [startH, startM] = activity.start_time.split(':').map(Number);
            const [endH, endM] = activity.end_time.split(':').map(Number);
            let diff = (endH * 60 + endM) - (startH * 60 + startM);
            if (diff < 0) diff += 24 * 60;
            duration = diff;
        }
        formState = {
            title: activity.title || '',
            notes: activity.notes || '',
            start_time: activity.start_time ? activity.start_time.slice(0, 5) : '',
            end_time: activity.end_time ? activity.end_time.slice(0, 5) : '',
            reference_ticket_no: activity.reference_ticket_no || '',
            booking_agency: activity.booking_agency || '',
            location: activity.location || '',
            duration: duration || 30,
            price: structured?.price || '',
            currency: structured?.currency || '',
        };
    } else if (typeStr === 'logging') {
        // Strip "Check-in:" / "Check-out:" prefixes from the title for the hotel name field
        const hotelNameRaw = activity.title || '';
        const hotelName = hotelNameRaw.replace(/^(Check-in|Check-out)[:\s]*/i, '');

        // For checkout activities derive check-in from structured_data if available
        const isCheckout = /^check-?out/i.test(hotelNameRaw);
        const checkInDateVal  = isCheckout
            ? (structured?.check_in_date_time  ? structured.check_in_date_time.split(' ')[0]  : '')
            : (activity.start_time_utc ? dayjs(activity.start_time_utc).format('YYYY-MM-DD') : (activity.start_time ? resolvedDay : ''));
        const checkInTimeVal  = isCheckout
            ? (structured?.check_in_date_time  ? structured.check_in_date_time.split(' ')[1]?.slice(0, 5) || '15:00' : '15:00')
            : (activity.start_time_utc ? dayjs(activity.start_time_utc).format('HH:mm') : (activity.start_time ? activity.start_time.slice(0, 5) : '15:00'));
        const checkOutDateVal = isCheckout
            ? (activity.start_time_utc ? dayjs(activity.start_time_utc).format('YYYY-MM-DD') : (activity.start_time ? resolvedDay : ''))
            : (activity.end_time_utc ? dayjs(activity.end_time_utc).format('YYYY-MM-DD') : (structured?.check_out_date_time ? structured.check_out_date_time.split(' ')[0] : resolvedDay));
        const checkOutTimeVal = isCheckout
            ? (activity.start_time_utc ? dayjs(activity.start_time_utc).format('HH:mm') : (activity.start_time ? activity.start_time.slice(0, 5) : '11:00'))
            : (activity.end_time_utc ? dayjs(activity.end_time_utc).format('HH:mm') : (structured?.check_out_date_time ? structured.check_out_date_time.split(' ')[1]?.slice(0, 5) || '11:00' : '11:00'));

        formState = {
            hotel_name: hotelName,
            address: structured?.address || structured?.location || '',
            booking_reference: structured?.booking_reference || activity.reference_ticket_no || '',
            agency_name: structured?.agency_name || '',
            notes: activity.notes || '',
            checkInDate: checkInDateVal,
            checkInTime: checkInTimeVal,
            checkOutDate: checkOutDateVal,
            checkOutTime: checkOutTimeVal,
            price: structured?.price || '',
            currency: structured?.currency || '',
            price_per_night: structured?.price_per_night || '',
        };
    } else if (typeStr === 'transportation') {
        if (activity.subtype === 'flight' || activity.type === 'flight') {
            const flightDetails = structured?.flight_details || structured?.details || {};
            const extractIata = (str) => {
                if (!str) return '';
                const m = str.match(/\(([A-Z]{3})\)/);
                if (m) return m[1];
                if (/^[A-Z]{3}$/.test(str.trim())) return str.trim();
                return '';
            };
            let activityDate = '';
            let arrivalDate = '';
            if (trip && activity.day) {
                const tripStartDate = new Date(trip.start_date);
                const activityDateObj = new Date(tripStartDate);
                activityDateObj.setDate(tripStartDate.getDate() + (activity.day - 1));
                activityDate = activityDateObj.toISOString().split('T')[0];
                arrivalDate = activityDate;
            }
            const departureOrigin =
                flightDetails?.departure?.iataCode || flightDetails?.departure_airport ||
                extractIata(flightDetails?.from) || structured?.departure_airport ||
                structured?.origin_airport_code || '';
            const departureDestination =
                flightDetails?.arrival?.iataCode || flightDetails?.arrival_airport ||
                extractIata(flightDetails?.to) || structured?.arrival_airport ||
                structured?.destination_airport_code || '';
            const carrierCode = flightDetails?.carrierCode || flightDetails?.carrier_code || structured?.carrier_name || '';
            const flightNumber = flightDetails?.flightNumber || flightDetails?.flight_number || structured?.flight_number || '';
            let departureDateTime = '';
            let arrivalDateTime = '';
            if (flightDetails?.departure?.at) departureDateTime = flightDetails.departure.at.slice(0, 16);
            else if (flightDetails?.departureDateTime) departureDateTime = flightDetails.departureDateTime.slice(0, 16);
            else if (structured?.start_date_time) departureDateTime = structured.start_date_time.replace(' ', 'T').slice(0, 16);
            else if (activity.start_time_utc) departureDateTime = activity.start_time_utc.slice(0, 16);
            else if (activity.start_time && activityDate) departureDateTime = `${activityDate}T${activity.start_time.slice(0, 5)}`;
            if (flightDetails?.arrival?.at) arrivalDateTime = flightDetails.arrival.at.slice(0, 16);
            else if (flightDetails?.arrivalDateTime) arrivalDateTime = flightDetails.arrivalDateTime.slice(0, 16);
            else if (structured?.end_date_time) arrivalDateTime = structured.end_date_time.replace(' ', 'T').slice(0, 16);
            else if (activity.end_time_utc) arrivalDateTime = activity.end_time_utc.slice(0, 16);
            else if (activity.end_time && arrivalDate) arrivalDateTime = `${arrivalDate}T${activity.end_time.slice(0, 5)}`;
            formState = {
                flightType: (structured?.flight_type === 'round-trip' || flightDetails?.flight_type === 'Return' || structured?.route_type === 'round-trip') ? 'round-trip' : 'one-way',
                booking_reference: structured?.booking_reference || activity.reference_ticket_no || '',
                additional_information: structured?.additional_information || '',
                departureFlight: { carrierCode, flightNumber, departureDateTime, arrivalDateTime, origin: departureOrigin, destination: departureDestination },
                price: structured?.price || '',
                currency: structured?.currency || '',
            };
        } else {
            let duration = activity.duration;
            if (!duration && activity.start_time && activity.end_time) {
                const [startH, startM] = activity.start_time.split(':').map(Number);
                const [endH, endM] = activity.end_time.split(':').map(Number);
                let diff = (endH * 60 + endM) - (startH * 60 + startM);
                if (diff < 0) diff += 24 * 60;
                duration = diff;
            }
            formState = {
                title: activity.title || '',
                notes: activity.notes || '',
                start_time: activity.start_time ? activity.start_time.slice(0, 5) : '',
                end_time: activity.end_time ? activity.end_time.slice(0, 5) : '',
                duration: duration || 30,
                price: structured?.price || '',
                currency: structured?.currency || '',
            };
        }
    } else {
        // Unknown type — fall back to activity fields
        formState = {
            title: activity.title || '',
            notes: activity.notes || '',
            start_time: activity.start_time ? activity.start_time.slice(0, 5) : '',
            duration: activity.duration || 30,
            location: activity.location || '',
        };
    }

    return { activityType: displayType, formState, selectedDay: resolvedDay, transportationType: transType, resolvedActivityType: resolvedType };
}

const AddActivityModal = ({ open, handleClose, tripId, trip, tripDays, selectedDay: initialSelectedDay, activity, isEdit, showLoggingOption = true }) => {
    const { t } = useTranslation();
    const inputCls = "tw-w-full tw-bg-surface-3/50 tw-border tw-border-surface-border/40 tw-rounded-lg tw-text-muted-fore tw-text-sm tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-border-accent/50 tw-transition-colors";
    const labelCls = "tw-text-xs tw-font-semibold tw-text-muted tw-uppercase tw-tracking-wide tw-block tw-mb-1";
    const hasFixedDates = Boolean(trip?.start_date && trip?.end_date);

    // Compute initial state directly from props so form is populated on first render (no flash)
    const [activityType, setActivityType] = useState(() =>
        isEdit && activity ? buildEditState(activity, tripDays, trip).activityType : 'Activity'
    );
    const [transportationType, setTransportationType] = useState(() =>
        isEdit && activity ? buildEditState(activity, tripDays, trip).transportationType : 'flight'
    );
    const [selectedDay, setSelectedDay] = useState(() =>
        isEdit && activity ? buildEditState(activity, tripDays, trip).selectedDay : ''
    );
    const [formState, setFormState] = useState(() =>
        isEdit && activity ? buildEditState(activity, tripDays, trip).formState : { duration: 30, flightType: 'one-way' }
    );
    const [resolvedActivityType, setResolvedActivityType] = useState(() =>
        isEdit && activity ? buildEditState(activity, tripDays, trip).resolvedActivityType : 'Activity'
    );

    // Reset form when opening for adding a new activity
    useEffect(() => {
        if (!open || isEdit) return;
        setActivityType('Activity');
        setResolvedActivityType('Activity');
        setTransportationType('flight');
        const initialDayObject = tripDays.find(d => d.dayNumber === initialSelectedDay);
        const fallbackDate = initialDayObject?.date || '';
        setSelectedDay(fallbackDate);
        const newFormState = {
            duration: 30,
            flightType: 'one-way',
            checkInDate: fallbackDate,
            checkOutDate: fallbackDate,
            checkInTime: '15:00',
            checkOutTime: '11:00',
            price: '',
            currency: '',
            price_per_night: '',
        };
        if (fallbackDate && !fallbackDate.startsWith('day-')) {
            newFormState.departureFlight = { departureDateTime: `${fallbackDate}T00:00` };
        }
        setFormState(newFormState);
    }, [open, isEdit, initialSelectedDay, tripDays]);
    const [sourceEmailMsg, setSourceEmailMsg] = useState(null);
    const [loadingSourceEmail, setLoadingSourceEmail] = useState(false);

    const handleViewSourceEmail = async () => {
        if (!activity?.source_message_id) return;
        setLoadingSourceEmail(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/get_message.php?id=${activity.source_message_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setSourceEmailMsg(await res.json());
        } catch (err) {
            console.error('Failed to load source email', err);
        } finally {
            setLoadingSourceEmail(false);
        }
    };

    const [carrierOptions, setCarrierOptions] = useState([]);
    const [departureFlightDetails, setDepartureFlightDetails] = useState(null);
    const [returnFlightDetails, setReturnFlightDetails] = useState(null);
    const [departureFlightOptions, setDepartureFlightOptions] = useState([]);
    const [returnFlightOptions, setReturnFlightOptions] = useState([]);
    const [departureFlightNotFound, setDepartureFlightNotFound] = useState(false);
    const [returnFlightNotFound, setReturnFlightNotFound] = useState(false);
    const [isFetchingDeparture, setIsFetchingDeparture] = useState(false);
    const [isFetchingReturn, setIsFetchingReturn] = useState(false);
    const [carrierInputValue, setCarrierInputValue] = useState('');

    const fetchAirlines = useCallback(debounce((input, callback) => {
        if (input.length < 2) {
            callback([]);
            return;
        }
        // Fetch airlines from backend API folder
        fetch(`${API_PATH}/airline_codes.json`)
            .then(response => response.json())
            .then(airlineCodesData => {
                const searchTerm = input.toLowerCase().trim();
                const searchLength = searchTerm.length;
                
                // Helper function to normalize text for comparison (remove " air" and " air ")
                const normalizeText = (text) => {
                    return text.toLowerCase()
                        .replace(/\bair\b/g, '')  // Remove standalone "air"
                        .replace(/\s+/g, ' ')     // Normalize spaces
                        .trim();
                };
                
                let results = [];
                
                if (searchLength === 2) {
                    // After 2nd character: Only check airline code (IATA)
                    results = airlineCodesData.filter(airline => 
                        airline.code.toLowerCase() === searchTerm
                    );
                } else if (searchLength === 3) {
                    // After 3rd character: 
                    // 1. If exact match on code, show it first
                    // 2. Then show airlines where name or country contains the string
                    const exactMatch = airlineCodesData.find(airline => 
                        airline.code.toLowerCase() === searchTerm
                    );
                    
                    const partialMatches = airlineCodesData.filter(airline => {
                        const normalizedName = normalizeText(airline.name);
                        const normalizedCountry = airline.country ? normalizeText(airline.country) : '';
                        const normalizedSearch = normalizeText(searchTerm);
                        
                        return (
                            airline.code.toLowerCase() !== searchTerm && // Exclude exact match (will be added first)
                            (normalizedName.includes(normalizedSearch) || 
                             normalizedCountry.includes(normalizedSearch))
                        );
                    });
                    
                    // Sort partial matches alphabetically by name
                    partialMatches.sort((a, b) => a.name.localeCompare(b.name));
                    
                    // Combine: exact match first, then sorted partial matches
                    results = exactMatch ? [exactMatch, ...partialMatches] : partialMatches;
                    
                } else if (searchLength >= 4) {
                    // From 4th character onwards:
                    // Show airlines where name or country contains the string (ignoring "air")
                    results = airlineCodesData.filter(airline => {
                        const normalizedName = normalizeText(airline.name);
                        const normalizedCountry = airline.country ? normalizeText(airline.country) : '';
                        const normalizedSearch = normalizeText(searchTerm);
                        
                        return (
                            normalizedName.includes(normalizedSearch) || 
                            normalizedCountry.includes(normalizedSearch) ||
                            airline.code.toLowerCase().includes(searchTerm)
                        );
                    });
                    
                    // Sort alphabetically by name
                    results.sort((a, b) => a.name.localeCompare(b.name));
                }
                
                // Limit to 50 results and format
                const filtered = results.slice(0, 50).map(airline => ({
                    label: `${airline.name} (${airline.code})`,
                    code: airline.code,
                }));
                
                callback(filtered);
            })
            .catch(err => {
                console.error('Failed to load airline codes:', err);
                callback([]);
            });
    }, 300), []);

    useEffect(() => {
        fetchAirlines(carrierInputValue, (options) => {
            setCarrierOptions(options);
        });
    }, [carrierInputValue, fetchAirlines]);

    // Remove duplicate reset - already handled in main useEffect above
    useEffect(() => {
        // Only reset flight-related state when opening modal for adding new activity (not editing)
        if (open && !isEdit) {
            setDepartureFlightDetails(null);
            setReturnFlightDetails(null);
            setDepartureFlightOptions([]);
            setReturnFlightOptions([]);
            setDepartureFlightNotFound(false);
            setReturnFlightNotFound(false);
        }
    }, [open, isEdit]);

    useEffect(() => {
        if (formState.flightType === 'round-trip' && departureFlightDetails && departureFlightDetails.arrival && departureFlightDetails.arrival.at) {
            const arrivalDate = departureFlightDetails.arrival.at.split('T')[0];
            setFormState(prevState => ({
                ...prevState,
                returnFlight: {
                    ...prevState.returnFlight,
                    departureDateTime: `${arrivalDate}T00:00`,
                },
            }));
        }
    }, [departureFlightDetails, formState.flightType]);

    const handleTypeChange = (event) => {
        const { value } = event.target;
        if (value === 'Logging' && !showLoggingOption) {
            return;
        }
        setActivityType(value);
    };

    const handleTransportationTypeChange = (event) => {
        setTransportationType(event.target.value);
    };

    const handleDayChange = (event) => {
        setSelectedDay(event.target.value);
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        const newFormState = { ...formState };
        if (name === 'departureDateTime' || name === 'arrivalDateTime') {
            if (!newFormState.departureFlight) newFormState.departureFlight = {};
            newFormState.departureFlight[name === 'departureDateTime' ? 'departureDateTime' : 'arrivalDateTime'] = value;
        } else {
            newFormState[name] = value;
        }
        if (name === 'start_time') {
            const startTime = value;
            const duration = parseInt(newFormState.duration) || 30;
            const [hours, minutes] = startTime.split(':').map(Number);
            const endDate = new Date(0, 0, 0, hours, minutes + duration);
            const endHours = String(endDate.getHours()).padStart(2, '0');
            const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
            newFormState.end_time = `${endHours}:${endMinutes}`;
        }
        if (name === 'duration') {
            const duration = parseInt(value) || 0;
            const startTime = newFormState.start_time;
            if (startTime) {
                const [hours, minutes] = startTime.split(':').map(Number);
                const endDate = new Date(0, 0, 0, hours, minutes + duration);
                const endHours = String(endDate.getHours()).padStart(2, '0');
                const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
                newFormState.end_time = `${endHours}:${endMinutes}`;
            }
        }
        setFormState(newFormState);
    };

    const handleFetchFlightDetails = async (flightType) => {
        const isReturn = flightType === 'return';
        const flightStateKey = isReturn ? 'returnFlight' : 'departureFlight';
        const flightState = formState[flightStateKey] || {};
        const departureDate = flightState.departureDateTime ? flightState.departureDateTime.split('T')[0] : null;

        // Helper function to extract IATA code from strings like "VACLAV HAVEL AIRPORT (PRG)" or just "PRG"
        const extractIataCode = (str) => {
            if (!str) return '';
            // Check if there's a code in parentheses like (PRG)
            const match = str.match(/\(([A-Z]{3})\)/);
            if (match) return match[1];
            // If the string is already a 3-letter code
            if (/^[A-Z]{3}$/.test(str.trim().toUpperCase())) {
                return str.trim().toUpperCase();
            }
            // Return the original string uppercase (might work for some cases)
            return str.trim().toUpperCase();
        };

        // Get origin and destination - use form state values, or fall back to trip defaults
        const getDefaultOrigin = () => {
            if (isReturn) {
                return extractIataCode(trip?.start_location || '');
            }
            return extractIataCode(trip?.home_airport || '');
        };
        
        const getDefaultDestination = () => {
            if (isReturn) {
                return extractIataCode(trip?.home_airport || '');
            }
            return extractIataCode(trip?.start_location || '');
        };
        
        // For form state values, also try to extract IATA code in case user pasted full name
        const origin = extractIataCode(flightState.origin) || getDefaultOrigin();
        const destination = extractIataCode(flightState.destination) || getDefaultDestination();

        // We need at minimum: origin, destination, and date
        if (!origin || !destination || !departureDate) {
            console.log('Missing required fields: origin, destination, or date', { origin, destination, departureDate });
            return;
        }

        // Set loading state
        if (isReturn) {
            setIsFetchingReturn(true);
            setReturnFlightNotFound(false);
            setReturnFlightOptions([]);
        } else {
            setIsFetchingDeparture(true);
            setDepartureFlightNotFound(false);
            setDepartureFlightOptions([]);
        }

        // Build URL with available parameters
        const params = new URLSearchParams({
            origin: origin,
            destination: destination,
            date: departureDate
        });
        
        // Add optional carrier code if provided
        if (flightState.carrierCode) {
            params.append('carrierCode', flightState.carrierCode);
        }
        
        // Add optional flight number if provided
        if (flightState.flightNumber) {
            params.append('flightNumber', flightState.flightNumber);
        }

        const url = `${API_PATH}/search_flights_for_route.php?${params.toString()}`;
        console.log('Searching flights:', url);

        try {
            const response = await fetch(url);
            const flightDetailsResponse = await response.json();
            
            if (response.ok && flightDetailsResponse.success && flightDetailsResponse.data?.length > 0) {
                const flightData = flightDetailsResponse.data;
                if (isReturn) {
                    if (flightData.length > 1) {
                        setReturnFlightOptions(flightData);
                        setReturnFlightDetails(null);
                    } else {
                        setReturnFlightDetails(flightData[0]);
                        setReturnFlightOptions([]);
                    }
                } else {
                    if (flightData.length > 1) {
                        setDepartureFlightOptions(flightData);
                        setDepartureFlightDetails(null);
                    } else {
                        setDepartureFlightDetails(flightData[0]);
                        setDepartureFlightOptions([]);
                    }
                }
            } else {
                console.error('No flights found or error:', flightDetailsResponse);
                if (isReturn) {
                    setReturnFlightNotFound(true);
                } else {
                    setDepartureFlightNotFound(true);
                }
            }
        } catch (error) {
            console.error('Error fetching flight details:', error);
            if (isReturn) {
                setReturnFlightNotFound(true);
            } else {
                setDepartureFlightNotFound(true);
            }
        }

        if (isReturn) {
            setIsFetchingReturn(false);
        } else {
            setIsFetchingDeparture(false);
        }
    };

    const handleSubmit = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert(t('auth_error'));
            return;
        }

        const dayObject = tripDays.find(d => d.date === selectedDay);
        if (!dayObject && !(activityType === 'Transportation' && transportationType === 'flight')) {
            alert(t('select_day_error'));
            return;
        }

        let activityToSave = null;
        const baseActivity = {
            trip_id: tripId,
            is_ai_suggestion: false,
            booking_reference: formState.booking_reference,
            additional_information: formState.additional_information,
        };

        // Preserve any existing structured_data on edit, and fold in the
        // traveller's price/cost fields (cleared fields are removed).
        const existingStructured = (() => {
            let s = activity?.structured_data;
            if (Array.isArray(s)) s = s.length ? s[0] : {};
            if (typeof s === 'string') { try { s = JSON.parse(s); } catch (e) { s = {}; } }
            return (s && typeof s === 'object') ? s : {};
        })();
        const applyPriceFields = (target) => {
            const out = { ...target };
            const set = (k, v) => {
                const val = (v === undefined || v === null) ? '' : String(v).trim();
                if (val === '') delete out[k]; else out[k] = val;
            };
            set('price', formState.price);
            set('currency', formState.currency);
            if (activityType === 'Logging') set('price_per_night', formState.price_per_night);
            return out;
        };

        if (activityType === 'Activity') {
            activityToSave = {
                ...baseActivity,
                day: dayObject.dayNumber,
                type: 'activity',
                title: formState.title,
                notes: formState.notes,
                start_time_utc: `${selectedDay}T${formState.start_time || '00:00'}:00`,
                end_time_utc: `${selectedDay}T${formState.end_time || '00:00'}:00`,
                location: formState.location || '',
                reference_ticket_no: formState.reference_ticket_no || '',
                booking_agency: formState.booking_agency || '',
                structured_data: applyPriceFields(existingStructured),
            };
        } else if (activityType === 'Logging') {
            // Detect check-in vs check-out to handle single-time-per-row model
            const originalLodgingTitle = activity?.title || '';
            const isCheckoutActivity = /^check-?out/i.test(originalLodgingTitle);
            const isCheckinActivity  = /^check-?in/i.test(originalLodgingTitle);
            const lodgingTitlePrefix = isCheckoutActivity ? 'Check-out: ' : isCheckinActivity ? 'Check-in: ' : '';

            const hotelName = formState.hotel_name || '';
            const checkInTime  = formState.checkInTime  || '15:00';
            const checkOutTime = formState.checkOutTime || '11:00';
            const checkInDate  = formState.checkInDate  || selectedDay || dayObject?.date || '';
            const checkOutDate = formState.checkOutDate || checkInDate;

            const dateToDay = (dateStr) => {
                if (trip?.start_date && dateStr) {
                    const diff = dayjs(dateStr).startOf('day').diff(dayjs(trip.start_date).startOf('day'), 'day');
                    return diff >= 0 ? diff + 1 : 1;
                }
                return dayObject?.dayNumber || 1;
            };

            const lodgingStructuredData = applyPriceFields({
                ...existingStructured,
                check_in_date_time:  `${checkInDate} ${checkInTime}`,
                check_out_date_time: `${checkOutDate} ${checkOutTime}`,
                address: formState.address || '',
                booking_reference: formState.booking_reference || '',
                agency_name: formState.agency_name || '',
            });

            if (isEdit) {
                // Editing: single row — use its own relevant time only
                const relevantTime = isCheckoutActivity ? checkOutTime : checkInTime;
                const activityDate = selectedDay || dayObject?.date || '';
                activityToSave = {
                    ...baseActivity,
                    day: dateToDay(activityDate),
                    type: 'lodging',
                    title: lodgingTitlePrefix + hotelName,
                    start_time_utc: activityDate ? `${activityDate}T${relevantTime}:00` : '',
                    end_time_utc:   activityDate ? `${activityDate}T${relevantTime}:00` : '',
                    notes: formState.notes,
                    structured_data: lodgingStructuredData,
                };
            } else {
                // New entry: build two rows — check-in and check-out
                activityToSave = {
                    ...baseActivity,
                    day: dateToDay(checkInDate),
                    type: 'lodging',
                    title: `Check-in: ${hotelName}`,
                    start_time_utc: checkInDate ? `${checkInDate}T${checkInTime}:00` : '',
                    end_time_utc:   checkInDate ? `${checkInDate}T${checkInTime}:00` : '',
                    duration: 0,
                    notes: formState.notes,
                    structured_data: lodgingStructuredData,
                };
                // Store checkout row to POST after check-in
                activityToSave._checkoutRow = {
                    ...baseActivity,
                    day: dateToDay(checkOutDate),
                    type: 'lodging',
                    title: `Check-out: ${hotelName}`,
                    start_time_utc: checkOutDate ? `${checkOutDate}T${checkOutTime}:00` : '',
                    end_time_utc:   checkOutDate ? `${checkOutDate}T${checkOutTime}:00` : '',
                    duration: 0,
                    notes: formState.notes,
                    structured_data: lodgingStructuredData,
                };
            }
        } else if (activityType === 'Transportation') {
            if (transportationType === 'flight') {
                const departureFlight = formState.departureFlight || {};
                const flightDetails = departureFlightDetails || departureFlight;
                // Ensure all required fields are present
                let start_time_utc = flightDetails.departure?.at || flightDetails.departureDateTime;
                let end_time_utc = flightDetails.arrival?.at || flightDetails.arrivalDateTime;
                let flightDate = start_time_utc ? start_time_utc.split('T')[0] : null;
                let flightDay = tripDays.find(d => d.date === flightDate);
                let dayNum = flightDay ? flightDay.dayNumber : (activity?.day || dayObject.dayNumber);
                
                // Preserve original title if editing, or build from route info
                // Priority: 1. Use existing title if it's a route format, 2. Use origin/destination from form, 3. Use from flightDetails, 4. Use carrier+number
                let title = activity?.title || '';
                const originCode = departureFlight.origin || flightDetails.departure?.iataCode || '';
                const destCode = departureFlight.destination || flightDetails.arrival?.iataCode || '';
                
                // Only update title if we have route info and it's not already a route-style title
                if (originCode && destCode) {
                    title = `${originCode} → ${destCode}`;
                } else if (!title || title.startsWith('Flight:')) {
                    // Fallback to carrier+number if no route info and no good existing title
                    const carrier = flightDetails.carrierCode || flightDetails.flightDesignator?.carrierCode || '';
                    const flightNum = flightDetails.flightNumber || flightDetails.flightDesignator?.flightNumber || '';
                    if (carrier || flightNum) {
                        title = `Flight: ${carrier} ${flightNum}`.trim();
                    }
                }
                // Extract start_time as 'HH:MM:SS'
                let start_time = (flightDetails.departure?.at || flightDetails.departureDateTime || activity?.start_time || '').split('T')[1]?.split('+')[0] || activity?.start_time || '';
                if (start_time && start_time.length === 5) start_time += ':00';
                // Extract end_time as 'HH:MM:SS'
                let end_time = (flightDetails.arrival?.at || flightDetails.arrivalDateTime || activity?.end_time || '').split('T')[1]?.split('+')[0] || activity?.end_time || '';
                if (end_time && end_time.length === 5) end_time += ':00';
                // Extract duration (in minutes if possible)
                let duration = activity?.duration || 0;
                if (start_time_utc && end_time_utc) {
                    // Calculate duration from datetime strings (works for both API and manual entry)
                    const dep = new Date(start_time_utc);
                    const arr = new Date(end_time_utc);
                    if (!isNaN(dep) && !isNaN(arr)) {
                        duration = Math.round((arr - dep) / 60000);
                    }
                }
                activityToSave = {
                    ...baseActivity,
                    user_id: activity?.user_id,
                    day: dayNum,
                    type: 'transportation',
                    subtype: 'flight',
                    source: activity?.source || 'manual',
                    title: title,
                    notes: activity?.notes || '',
                    start_time: start_time,
                    end_time: end_time,
                    start_time_utc: start_time_utc,
                    end_time_utc: end_time_utc,
                    duration: duration,
                    location: activity?.location || '',
                    is_ai_suggestion: activity?.is_ai_suggestion || 0,
                    is_pinned: activity?.is_pinned || 0,
                    trip_id: activity?.trip_id || tripId,
                    structured_data: applyPriceFields({
                        flight_type: formState.flightType,
                        source: flightDetails.source || 'manual',
                        details: {
                            ...flightDetails,
                            seatNumber: departureFlight.seatNumber
                        }
                    })
                };
            } else { // Other transportation
                activityToSave = {
                    ...baseActivity,
                    day: dayObject.dayNumber,
                    type: 'transportation',
                    subtype: transportationType,
                    title: formState.title,
                    notes: formState.notes,
                    start_time_utc: `${selectedDay}T${formState.start_time || '00:00'}:00`,
                    end_time_utc: `${selectedDay}T${formState.end_time || '00:00'}:00`,
                    structured_data: applyPriceFields(existingStructured),
                };
            }
        }

        if (!activityToSave) {
            handleClose();
            return;
        }

        try {
            let response;
            if (isEdit && activity && activity.activity_id) {
                // Build update payload from latest form state and activity for required fields
                const allowedFields = [
                    'title', 'notes', 'start_time', 'start_time_utc', 'end_time_utc',
                    'duration', 'location', 'is_ai_suggestion', 'day', 'is_pinned', 
                    'reference_ticket_no', 'booking_agency', 'structured_data'
                ];
                const updatePayload = {};
                allowedFields.forEach(key => {
                    // Prefer formState, then activityToSave, then activity
                    let value = formState[key] !== undefined ? formState[key] : (activityToSave[key] !== undefined ? activityToSave[key] : (activity[key] !== undefined ? activity[key] : ''));
                    // Ensure start_time is always HH:MM:SS
                    if (key === 'start_time') {
                        if (typeof value === 'string' && value.length === 5 && value.match(/^\d{2}:\d{2}$/)) {
                            value += ':00';
                        }
                        if (!value) value = '00:00:00';
                    }
                    // Ensure is_ai_suggestion and is_pinned are integers
                    if (key === 'is_ai_suggestion' || key === 'is_pinned') {
                        value = value === true ? 1 : value === false ? 0 : parseInt(value) || 0;
                    }
                    updatePayload[key] = value;
                });
                const updateUrl = `${API_PATH}/activities.php?id=${activity.activity_id}`;
                const updateOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(updatePayload),
                };
                // Simulate SQL statement for debugging
                let sqlSet = Object.entries(updatePayload)
                    .filter(([key]) => key !== 'structured_data')
                    .map(([key, value]) => `${key} = ${typeof value === 'string' ? `'${value}'` : value}`)
                    .join(', ');
                response = await fetch(updateUrl, updateOptions);
                
                // After successful update, cascade time changes to subsequent activities
                if (response.ok && activityToSave.start_time) {
                    try {
                        await fetch(`${API_PATH}/cascade_activity_times.php`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                activity_id: activity.activity_id,
                                trip_id: tripId,
                                gap_minutes: 30
                            }),
                        });
                    } catch (cascadeErr) {
                        console.log('Note: Could not cascade time updates:', cascadeErr);
                    }
                }
            } else {
                // Add new activity
                const checkoutRow = activityToSave._checkoutRow || null;
                const { _checkoutRow, ...activityToPost } = activityToSave;
                const addUrl = `${API_PATH}/activities.php`;
                const addOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(activityToPost),
                };
                response = await fetch(addUrl, addOptions);
                // For lodging: also POST the check-out row
                if (response.ok && checkoutRow) {
                    try {
                        await fetch(addUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify(checkoutRow),
                        });
                    } catch (e) { console.error('Failed to save check-out row:', e); }
                }
                
                // After successful add, cascade time changes to subsequent activities
                if (response.ok) {
                    try {
                        const responseData = await response.clone().json();
                        if (responseData.activity_id) {
                            await fetch(`${API_PATH}/cascade_activity_times.php`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`,
                                },
                                body: JSON.stringify({
                                    activity_id: responseData.activity_id,
                                    trip_id: tripId,
                                    gap_minutes: 30
                                }),
                            });
                        }
                    } catch (cascadeErr) {
                        console.log('Note: Could not cascade time updates:', cascadeErr);
                    }
                }
            }
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `${t('save_failed_error')}${activityToSave.title}`);
            }
            handleClose(); // This will trigger a refresh in the parent.
        } catch (err) {
            console.error('Failed to save activity:', err);
            alert(`${t('generic_error')}${err.message}`);
        }
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-GB', { month: 'short' });
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    // Price / cost inputs — shared across activity, lodging and transportation forms.
    const renderPriceFields = (includePerNight = false) => (
        <div className={`tw-grid ${includePerNight ? 'tw-grid-cols-3' : 'tw-grid-cols-2'} tw-gap-3`}>
            <div>
                <label className={labelCls}>{t('price_label') || 'Price'}</label>
                <input
                    name="price"
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputCls}
                    onChange={handleFormChange}
                    value={formState.price || ''}
                    placeholder="e.g. 1240"
                />
            </div>
            {includePerNight && (
                <div>
                    <label className={labelCls}>{t('price_per_night_label') || 'Per night'}</label>
                    <input
                        name="price_per_night"
                        type="number"
                        min="0"
                        step="0.01"
                        className={inputCls}
                        onChange={handleFormChange}
                        value={formState.price_per_night || ''}
                        placeholder="optional"
                    />
                </div>
            )}
            <div>
                <label className={labelCls}>{t('currency_label') || 'Currency'}</label>
                <input
                    name="currency"
                    className={inputCls}
                    onChange={handleFormChange}
                    value={formState.currency || ''}
                    placeholder="USD"
                />
            </div>
        </div>
    );

    const renderActivityForm = () => (
        <div className="tw-flex tw-flex-col tw-gap-3">
            <div>
                <label className={labelCls}>{t('activity_name_label')}</label>
                <input name="title" className={inputCls} onChange={handleFormChange} required value={formState.title || ''} />
            </div>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                <div>
                    <label className={labelCls}>{t('start_time_label')}</label>
                    <input name="start_time" type="time" className={inputCls} onChange={handleFormChange} required value={formState.start_time || ''} />
                </div>
                <div>
                    <label className={labelCls}>{t('duration_minutes_label') || 'Duration (min)'}</label>
                    <input name="duration" type="number" min="0" className={inputCls} onChange={handleFormChange} value={formState.duration || ''} />
                </div>
            </div>
            <div>
                <label className={labelCls}>{t('activity_details_label')}</label>
                <textarea name="notes" rows={3} className={`${inputCls} tw-resize-none`} onChange={handleFormChange} value={formState.notes || ''} />
            </div>
            <div>
                <label className={labelCls}>{t('reference_ticket_label')}</label>
                <input name="reference_ticket_no" className={inputCls} onChange={handleFormChange} value={formState.reference_ticket_no || ''} />
            </div>
            <div>
                <label className={labelCls}>{t('booking_info_label')}</label>
                <input name="booking_agency" className={inputCls} onChange={handleFormChange} value={formState.booking_agency || ''} />
            </div>
            <div>
                <label className={labelCls}>{t('location_label') || 'Location'}</label>
                <input name="location" className={inputCls} onChange={handleFormChange} value={formState.location || ''} placeholder="e.g. Paris, France" />
            </div>
            {renderPriceFields()}
        </div>
    );

    const renderLodgingForm = () => {
        const lodgingTitle = activity?.title || formState.hotel_name || '';
        const isCheckoutRow = /^check-?out/i.test(lodgingTitle);
        const isCheckinRow  = /^check-?in/i.test(lodgingTitle);
        // For new (non-edit) lodging entries show both; for existing rows show only the relevant time
        const showOnlyCheckout = isEdit && isCheckoutRow;
        const showOnlyCheckin  = isEdit && isCheckinRow;
        return (
        <div className="tw-flex tw-flex-col tw-gap-3">
            <div>
                <label className={labelCls}>{t('hotel_name_label')}</label>
                <input name="hotel_name" className={inputCls} onChange={handleFormChange} required value={formState.hotel_name || ''} />
            </div>
            <div>
                <label className={labelCls}>{t('address_label')}</label>
                <input name="address" className={inputCls} onChange={handleFormChange} value={formState.address || ''} />
            </div>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                <div>
                    <label className={labelCls}>{t('booking_reference_label')}</label>
                    <input name="booking_reference" className={inputCls} onChange={handleFormChange} value={formState.booking_reference || ''} />
                </div>
                <div>
                    <label className={labelCls}>{t('booking_agency_label')}</label>
                    <input name="agency_name" className={inputCls} onChange={handleFormChange} value={formState.agency_name || ''} />
                </div>
            </div>
            {/* Editing check-in row: show only check-in time */}
            {/* Editing check-out row: show only check-out time */}
            {/* New entry: show both with date + time */}
            {!showOnlyCheckout && (
                <div className="tw-p-3 tw-rounded-xl tw-bg-surface-3/40 tw-border tw-border-surface-border/30">
                    <p className="tw-text-xs tw-font-semibold tw-text-muted tw-uppercase tw-tracking-wide tw-mb-2">Check-in</p>
                    <div className={`tw-grid tw-gap-3 ${!isEdit ? 'tw-grid-cols-2' : 'tw-grid-cols-1'}`}>
                        {!isEdit && (
                            <div>
                                <label className={labelCls}>Date</label>
                                <input name="checkInDate" type="date" className={inputCls} value={formState.checkInDate || ''} onChange={handleFormChange} />
                            </div>
                        )}
                        <div>
                            <label className={labelCls}>Time</label>
                            <input name="checkInTime" type="time" className={inputCls} value={formState.checkInTime || '15:00'} onChange={handleFormChange} />
                        </div>
                    </div>
                </div>
            )}
            {!showOnlyCheckin && (
                <div className="tw-p-3 tw-rounded-xl tw-bg-surface-3/40 tw-border tw-border-surface-border/30">
                    <p className="tw-text-xs tw-font-semibold tw-text-muted tw-uppercase tw-tracking-wide tw-mb-2">Check-out</p>
                    <div className={`tw-grid tw-gap-3 ${!isEdit ? 'tw-grid-cols-2' : 'tw-grid-cols-1'}`}>
                        {!isEdit && (
                            <div>
                                <label className={labelCls}>Date</label>
                                <input name="checkOutDate" type="date" className={inputCls} value={formState.checkOutDate || ''} onChange={handleFormChange} />
                            </div>
                        )}
                        <div>
                            <label className={labelCls}>Time</label>
                            <input name="checkOutTime" type="time" className={inputCls} value={formState.checkOutTime || '11:00'} onChange={handleFormChange} />
                        </div>
                    </div>
                </div>
            )}
            {renderPriceFields(true)}
            <div>
                <label className={labelCls}>{t('other_notes_label')}</label>
                <textarea name="notes" rows={3} className={`${inputCls} tw-resize-none`} onChange={handleFormChange} value={formState.notes || ''} />
            </div>
        </div>
        );
    }

    const renderFlightFields = (flightType) => {
        const isReturn = flightType === 'return';
        const flightDetails = isReturn ? returnFlightDetails : departureFlightDetails;
        const flightOptions = isReturn ? returnFlightOptions : departureFlightOptions;
        const flightNotFound = isReturn ? returnFlightNotFound : departureFlightNotFound;
        const isFetching = isReturn ? isFetchingReturn : isFetchingDeparture;
        const statePrefix = isReturn ? 'returnFlight' : 'departureFlight';

    // Extract values from departureFlight object if present
    const flightObj = formState[statePrefix] || {};
    // Prefer top-level carrierCode, then flightDesignator.carrierCode
    const carrierCode = flightObj.carrierCode || (flightObj.flightDesignator?.carrierCode) || '';
    const flightNumber = flightObj.flightDesignator?.flightNumber || flightObj.flightNumber || '';
    
    // Helper function to extract IATA code from strings like "VACLAV HAVEL AIRPORT (PRG)" or just "PRG"
    const extractIataCode = (str) => {
        if (!str) return '';
        // Check if there's a code in parentheses like (PRG)
        const match = str.match(/\(([A-Z]{3})\)/);
        if (match) return match[1];
        // If the string is already a 3-letter code
        if (/^[A-Z]{3}$/.test(str.trim().toUpperCase())) {
            return str.trim().toUpperCase();
        }
        // Return empty - we need a valid IATA code
        return '';
    };
    
    // Get origin/destination from flight state, with defaults from trip data (extracted as IATA codes)
    const getDefaultOrigin = () => {
        if (isReturn) {
            // For return: from destination back to home
            return extractIataCode(trip?.start_location || '');
        }
        // For departure: from home to destination
        return extractIataCode(trip?.home_airport || '');
    };
    
    const getDefaultDestination = () => {
        if (isReturn) {
            // For return: back to home
            return extractIataCode(trip?.home_airport || '');
        }
        // For departure: to destination
        return extractIataCode(trip?.start_location || '');
    };
    
    const origin = flightObj.origin || getDefaultOrigin();
    const destination = flightObj.destination || getDefaultDestination();
    
        // Format date for datetime-local input (YYYY-MM-DDTHH:mm)
        const formatDateLocal = (iso) => {
            if (!iso) return '';
            // Remove timezone if present
            const match = iso.match(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
            return match ? match[1] : iso;
        };
        
        // Get the default date - use selectedDay if available, otherwise trip start date
        const getDefaultDate = () => {
            if (selectedDay) return `${selectedDay}T09:00`;
            if (trip?.start_date) return `${trip.start_date}T09:00`;
            return '';
        };
        
        const departureDateTime = formatDateLocal(flightObj.departure?.at || flightObj.departureDateTime || '') || getDefaultDate();
        const arrivalDateTime = formatDateLocal(flightObj.arrival?.at || flightObj.arrivalDateTime || '');

        const handleFlightFormChange = (e, value, fieldName) => {
            const newFormState = { ...formState };
            if (!newFormState[statePrefix]) {
                newFormState[statePrefix] = {};
            }

            if (fieldName) {
                newFormState[statePrefix][fieldName] = value ? value.code : '';
            } else {
                let fieldValue = e.target.value;
                // Convert origin and destination to uppercase
                if (e.target.name === 'origin' || e.target.name === 'destination') {
                    fieldValue = fieldValue.toUpperCase();
                }
                newFormState[statePrefix][e.target.name] = fieldValue;
            }

            setFormState(newFormState);
        };

        const handleFlightOptionChange = (event) => {
            const selectedIndex = event.target.value;
            if (selectedIndex === '') return;

            if (isReturn) {
                setReturnFlightDetails(returnFlightOptions[selectedIndex]);
            } else {
                setDepartureFlightDetails(departureFlightOptions[selectedIndex]);
            }
        };

        // If carrierCode is present but not in carrierOptions, add it for display
        const carrierValue = carrierOptions.find(option => option.code === carrierCode) || (carrierCode ? { code: carrierCode, label: carrierCode } : null);

        return (
            <div className="tw-mt-3 tw-p-3 tw-rounded-xl tw-border tw-border-surface-border/40 tw-bg-surface-3/20">
                <p className="tw-text-sm tw-font-bold tw-text-muted-fore tw-mb-3">
                    {isReturn ? t('return_flight_title') : t('departure_flight_title')}
                </p>

                {/* Origin / Destination */}
                <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
                    <div>
                        <label className={labelCls}>{t('origin_airport_label') || 'Origin (IATA)'}</label>
                        <input
                            name="origin"
                            className={inputCls}
                            value={origin}
                            onChange={(e) => handleFlightFormChange(e)}
                            placeholder="e.g. JFK"
                            disabled={!!flightDetails || flightOptions.length > 0}
                            maxLength={3}
                            style={{ textTransform: 'uppercase' }}
                            required
                        />
                    </div>
                    <div>
                        <label className={labelCls}>{t('destination_airport_label') || 'Destination (IATA)'}</label>
                        <input
                            name="destination"
                            className={inputCls}
                            value={destination}
                            onChange={(e) => handleFlightFormChange(e)}
                            placeholder="e.g. LAX"
                            disabled={!!flightDetails || flightOptions.length > 0}
                            maxLength={3}
                            style={{ textTransform: 'uppercase' }}
                            required
                        />
                    </div>
                </div>

                {/* Carrier Autocomplete */}
                <div className="tw-mb-3">
                    <label className={labelCls}>{t('carrier_name_code_label')}</label>
                    <Autocomplete
                        freeSolo
                        options={carrierOptions.concat(carrierValue && !carrierOptions.some(o => o.code === carrierCode) ? [carrierValue] : [])}
                        getOptionLabel={(option) => option.label || option.code || ''}
                        value={carrierValue}
                        onChange={(e, value) => handleFlightFormChange(e, value, 'carrierCode')}
                        onInputChange={(e, newInputValue, reason) => {
                            if (reason === 'input') {
                                setCarrierInputValue(newInputValue);
                                const newFormState = { ...formState };
                                if (!newFormState[statePrefix]) newFormState[statePrefix] = {};
                                newFormState[statePrefix]['carrierCode'] = newInputValue;
                                setFormState(newFormState);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                name="carrierCode"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                        borderRadius: '8px',
                                        color: 'var(--color-muted-fore, #e2e8f0)',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                                        '&:hover fieldset': { borderColor: 'var(--color-accent, #6366f1)' },
                                        '&.Mui-focused fieldset': { borderColor: 'var(--color-accent, #6366f1)' },
                                    },
                                    '& .MuiInputBase-input': { color: 'var(--color-muted-fore, #e2e8f0)', fontSize: '0.875rem' },
                                    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': { color: 'var(--color-muted, #94a3b8)' },
                                }}
                            />
                        )}
                        slotProps={{
                            paper: {
                                sx: {
                                    bgcolor: 'var(--color-surface-2, #1e293b)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    '& .MuiAutocomplete-option': {
                                        color: 'var(--color-muted-fore, #e2e8f0)',
                                        fontSize: '0.875rem',
                                        '&[aria-selected="true"]': { bgcolor: 'rgba(255,255,255,0.08)' },
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                                    },
                                },
                            },
                        }}
                        disabled={!!flightDetails || flightOptions.length > 0}
                    />
                </div>

                {/* Flight Number */}
                <div className="tw-mb-3">
                    <label className={labelCls}>{t('flight_number_label')}</label>
                    <input
                        name="flightNumber"
                        className={inputCls}
                        value={flightNumber}
                        onChange={(e) => handleFlightFormChange(e)}
                        disabled={!!flightDetails || flightOptions.length > 0}
                    />
                </div>

                {/* Fetch Button */}
                <button
                    type="button"
                    className="tw-w-full tw-py-2 tw-rounded-lg tw-text-sm tw-font-semibold tw-bg-accent/10 tw-text-accent tw-border tw-border-accent/30 hover:tw-bg-accent/20 tw-transition-all disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-mb-2"
                    onClick={() => handleFetchFlightDetails(flightType)}
                    disabled={isFetching || !!flightDetails || flightOptions.length > 0 || !origin || !destination || !departureDateTime}
                >
                    {isFetching ? (
                        <span className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                            <svg className="tw-animate-spin tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none">
                                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                            {t('fetch_flight_info_btn')}
                        </span>
                    ) : t('fetch_flight_info_btn')}
                </button>

                {flightNotFound && (
                    <p className="tw-text-xs tw-text-red-400 tw-mb-2">{t('flight_not_found_error')}</p>
                )}

                {/* Flight Options Cards */}
                {flightOptions.length > 0 && !flightDetails && (
                    <div className="tw-mt-1">
                        <p className="tw-text-xs tw-text-muted tw-mb-2">
                            {t('multiple_flights_found')} ({flightOptions.length} {t('flights_available') || 'flights available'})
                        </p>
                        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-mb-2">
                            {flightOptions.map((flight, index) => {
                                const carrierDisplay = flight.carrierName || flight.carrierCode || '';
                                const carrierCodeDisplay = flight.carrierCode || '';
                                const flightNum = flight.flightNumber || '';
                                const depTime = flight.departure?.at ? new Date(flight.departure.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                const arrTime = flight.arrival?.at ? new Date(flight.arrival.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                const depAirport = flight.departure?.iataCode || '';
                                const arrAirport = flight.arrival?.iataCode || '';
                                const formatDuration = (dur) => {
                                    if (!dur) return '';
                                    const match = dur.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                                    if (match) {
                                        const hours = match[1] ? `${match[1]}h` : '';
                                        const mins = match[2] ? `${match[2]}m` : '';
                                        return `${hours}${mins}`;
                                    }
                                    return dur;
                                };
                                const duration = formatDuration(flight.duration);
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => { if (isReturn) { setReturnFlightDetails(flight); } else { setDepartureFlightDetails(flight); } }}
                                        className="tw-w-full tw-text-left tw-p-2.5 tw-rounded-xl tw-bg-surface-3/50 tw-border tw-border-surface-border/40 hover:tw-border-accent/40 hover:tw-bg-surface-3 tw-transition-all"
                                    >
                                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-0.5">
                                            <span className="tw-text-sm tw-font-bold tw-text-muted-fore">{carrierCodeDisplay} {flightNum}</span>
                                            <span className="tw-text-xs tw-text-muted">{carrierDisplay}</span>
                                        </div>
                                        <div className="tw-flex tw-items-center tw-justify-between">
                                            <span className="tw-text-sm tw-text-muted-fore">
                                                {depAirport} <strong>{depTime}</strong> → {arrAirport} <strong>{arrTime}</strong>
                                            </span>
                                            {duration && <span className="tw-text-xs tw-text-accent tw-font-semibold">{duration}</span>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                if (isReturn) {
                                    setReturnFlightOptions([]);
                                    setReturnFlightDetails(null);
                                    setReturnFlightNotFound(false);
                                } else {
                                    setDepartureFlightOptions([]);
                                    setDepartureFlightDetails(null);
                                    setDepartureFlightNotFound(false);
                                }
                            }}
                            className="tw-w-full tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-muted hover:tw-text-muted-fore tw-border tw-border-surface-border/40 hover:tw-border-surface-border tw-transition-all"
                        >
                            {t('clear_search_btn') || 'Search Again'}
                        </button>
                    </div>
                )}

                {/* Confirmed flight or manual datetime entry */}
                {flightDetails ? (
                    <div className="tw-mt-2">
                        <div className="tw-p-2.5 tw-rounded-xl tw-bg-emerald-500/10 tw-border tw-border-emerald-500/25 tw-mb-2">
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="tw-text-emerald-400"><polyline points="20 6 9 17 4 12"/></svg>
                                <span className="tw-text-xs tw-font-semibold tw-text-emerald-400 tw-uppercase tw-tracking-wide">{t('departure_label')}</span>
                            </div>
                            <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">
                                {flightDetails.departure.iataCode} · {formatDateTime(flightDetails.departure.at)}
                            </p>
                            <p className="tw-text-xs tw-text-muted tw-mt-0.5">
                                → {flightDetails.arrival.iataCode} · {formatDateTime(flightDetails.arrival.at)}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                if (isReturn) {
                                    setReturnFlightDetails(null);
                                    setReturnFlightNotFound(false);
                                } else {
                                    setDepartureFlightDetails(null);
                                    setDepartureFlightNotFound(false);
                                }
                            }}
                            className="tw-w-full tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-muted hover:tw-text-muted-fore tw-border tw-border-surface-border/40 hover:tw-border-surface-border tw-transition-all"
                        >
                            {t('clear_selection_btn') || 'Change Flight'}
                        </button>
                    </div>
                ) : (
                    flightOptions.length === 0 && (
                        <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mt-2">
                            <div>
                                <label className={labelCls}>{t('departure_date_time_label')}</label>
                                <input
                                    name="departureDateTime"
                                    type="datetime-local"
                                    className={inputCls}
                                    value={departureDateTime}
                                    onChange={(e) => handleFlightFormChange(e)}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{t('arrival_date_time_label')}</label>
                                <input
                                    name="arrivalDateTime"
                                    type="datetime-local"
                                    className={inputCls}
                                    value={arrivalDateTime}
                                    min={departureDateTime}
                                    onChange={(e) => handleFlightFormChange(e)}
                                />
                            </div>
                        </div>
                    )
                )}

                {/* Seat Number */}
                <div className="tw-mt-3">
                    <label className={labelCls}>{t('seat_number_label')}</label>
                    <input
                        name="seatNumber"
                        className={inputCls}
                        value={flightObj.seatNumber || ''}
                        onChange={(e) => handleFlightFormChange(e)}
                    />
                </div>
            </div>
        );
    };

    const renderTransportationForm = () => {
        const transportTypes = [
            { value: 'flight', label: t('transportation_flight') },
            { value: 'train', label: t('transportation_train') },
            { value: 'bus', label: t('transportation_bus') },
            { value: 'boat_ferry', label: t('transportation_boat_ferry') },
            { value: 'other', label: t('transportation_other') },
        ];
        return (
            <div className="tw-flex tw-flex-col tw-gap-3">
                <div>
                    <label className={labelCls}>{t('transportation_type_label')}</label>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {transportTypes.map(({ value, label }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => handleTransportationTypeChange({ target: { value } })}
                                className={`tw-px-3 tw-py-1.5 tw-rounded-full tw-text-xs tw-font-semibold tw-transition-all ${
                                    transportationType === value
                                        ? 'tw-bg-accent tw-text-white'
                                        : 'tw-bg-surface-3/50 tw-text-muted hover:tw-text-muted-fore tw-border tw-border-surface-border/40'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {transportationType === 'flight' && (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        <div>
                            <label className={labelCls}>{t('flight_type_one_way') || 'Flight type'}</label>
                            <div className="tw-flex tw-gap-2">
                                {[{ value: 'one-way', label: t('flight_type_one_way') }, { value: 'round-trip', label: t('flight_type_round_trip') }].map(({ value, label }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => handleFormChange({ target: { name: 'flightType', value } })}
                                        className={`tw-px-3 tw-py-1.5 tw-rounded-full tw-text-xs tw-font-semibold tw-transition-all ${
                                            formState.flightType === value
                                                ? 'tw-bg-accent tw-text-white'
                                                : 'tw-bg-surface-3/50 tw-text-muted hover:tw-text-muted-fore tw-border tw-border-surface-border/40'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {renderFlightFields('departure')}
                        {formState.flightType === 'round-trip' && renderFlightFields('return')}
                        <div>
                            <label className={labelCls}>{t('booking_reference_label')}</label>
                            <input name="booking_reference" className={inputCls} onChange={handleFormChange} value={formState.booking_reference || ''} />
                        </div>
                        <div>
                            <label className={labelCls}>{t('additional_info_label')}</label>
                            <textarea name="additional_information" rows={2} className={`${inputCls} tw-resize-none`} onChange={handleFormChange} value={formState.additional_information || ''} />
                        </div>
                        {renderPriceFields()}
                    </div>
                )}

                {['train', 'bus', 'boat_ferry', 'other'].includes(transportationType) && renderActivityForm()}
            </div>
        );
    };

    const activityTypes = [
        { value: 'Activity', label: t('activity_type_activity') },
        ...(hasFixedDates && (showLoggingOption || (isEdit && resolvedActivityType === 'Logging'))
            ? [{ value: 'Logging', label: t('activity_type_logging'), disabled: !showLoggingOption }]
            : []),
        ...(hasFixedDates ? [{ value: 'Transportation', label: t('activity_type_transportation') }] : []),
    ];

    return (
        <>
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    backgroundImage: 'none',
                    maxHeight: '90vh',
                    backgroundColor: 'var(--color-surface-2, #1e2433)',
                    color: 'var(--color-muted-fore, #e2e8f0)',
                },
            }}
        >
            <DialogTitle sx={{ p: 0, backgroundColor: 'transparent' }}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-5 tw-pb-3 tw-border-b tw-border-surface-border/40">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Calendar size={15} className="tw-text-accent" />
                        <span className="tw-text-base tw-font-bold tw-text-muted-fore">
                            {isEdit ? t('edit_activity_title') || 'Edit Activity' : t('add_activity_modal_title')}
                        </span>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-1">
                        {isEdit && activity?.source_message_id && (
                            <Tooltip title="View source email" arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleViewSourceEmail}
                                    disabled={loadingSourceEmail}
                                    sx={{ color: 'rgba(108,99,255,0.85)', '&:hover': { color: '#6C63FF', bgcolor: 'rgba(108,99,255,0.12)' } }}
                                >
                                    {loadingSourceEmail
                                        ? <CircularProgress size={14} sx={{ color: '#6C63FF' }} />
                                        : <MailOutlineIcon sx={{ fontSize: '18px' }} />
                                    }
                                </IconButton>
                            </Tooltip>
                        )}
                        <button
                            onClick={handleClose}
                            aria-label="Close"
                            className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>
            </DialogTitle>
            <DialogContent sx={{ pt: 2, px: 2.5, pb: 1, backgroundColor: 'transparent', overflowY: 'auto' }}>
                {/* Day selector */}
                <div className="tw-mb-4">
                    <label className={labelCls}>{t('day_label')}</label>
                    <select
                        className={`${inputCls} tw-cursor-pointer`}
                        value={selectedDay}
                        onChange={handleDayChange}
                    >
                        {tripDays.map(day => (
                            <option key={day.date} value={day.date}>{day.label}</option>
                        ))}
                    </select>
                </div>

                {/* Activity type pills */}
                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                    {activityTypes.map(({ value, label, disabled }) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => handleTypeChange({ target: { value } })}
                            disabled={disabled}
                            className={`tw-px-3 tw-py-1.5 tw-rounded-full tw-text-xs tw-font-semibold tw-transition-all ${
                                activityType === value
                                    ? 'tw-bg-accent tw-text-white'
                                    : 'tw-bg-surface-3/50 tw-text-muted hover:tw-text-muted-fore tw-border tw-border-surface-border/40'
                            } disabled:tw-opacity-40 disabled:tw-cursor-not-allowed`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {!hasFixedDates && (
                    <p className="tw-text-xs tw-text-muted tw-mb-3">{t('logging_transportation_warning')}</p>
                )}

                {activityType === 'Activity' && renderActivityForm()}
                {activityType === 'Logging' && renderLodgingForm()}
                {activityType === 'Transportation' && renderTransportationForm()}
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, backgroundColor: 'transparent', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button
                    type="button"
                    onClick={handleClose}
                    className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all tw-border tw-border-surface-border/40"
                >
                    {t('cancel_btn')}
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="tw-px-5 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent tw-text-white hover:tw-opacity-90 tw-transition-all"
                >
                    {t('save_btn')}
                </button>
            </DialogActions>
        </Dialog>

        {/* Source email viewer */}
        <Dialog
            open={Boolean(sourceEmailMsg)}
            onClose={() => setSourceEmailMsg(null)}
            fullScreen
            sx={{ zIndex: 1500 }}
            PaperProps={{ style: { backgroundColor: '#1a1d2e', color: '#f0f0f5' } }}
        >
            <DialogTitle
                style={{ backgroundColor: '#242740', borderBottom: '1px solid rgba(255,255,255,0.10)' }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5 }}
            >
                <MailOutlineIcon sx={{ flexShrink: 0, color: '#6C63FF' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap style={{ color: '#f0f0f5' }}>
                        {sourceEmailMsg?.subject || '(no subject)'}
                    </Typography>
                    <Typography variant="caption" display="block" style={{ color: 'rgba(240,240,245,0.55)' }}>
                        From: {sourceEmailMsg?.sender_email}
                    </Typography>
                </Box>
                <IconButton onClick={() => setSourceEmailMsg(null)} size="small" sx={{ color: 'rgba(240,240,245,0.55)', flexShrink: 0 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }} style={{ backgroundColor: '#1a1d2e' }}>
                <Box sx={{ p: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.7 }} style={{ color: '#f0f0f5' }}>
                    {sourceEmailMsg?.body || ''}
                </Box>
            </DialogContent>
            <DialogActions style={{ backgroundColor: '#242740', borderTop: '1px solid rgba(255,255,255,0.10)' }}>
                <Button onClick={() => setSourceEmailMsg(null)} variant="contained" sx={{ bgcolor: '#6C63FF', color: '#fff', '&:hover': { bgcolor: '#574fd6' }, fontWeight: 600 }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default AddActivityModal;
